import { DateTimeStr, toDateTimeStr } from '@mi-front/core';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useState } from 'react';
import { DateRangePicker, DateRangePickerShape } from 'react-dates';
import 'react-dates/initialize';
import { useIntl } from 'react-intl';

const StyledDateRangeWrapper = styled('div')(({ theme }) => ({
  display: 'inline-block',
  position: 'relative',

  '& .SingleDatePickerInput, & .DateRangePickerInput': {
    borderRadius: '0.375rem', // $border-radius
  },

  '& .SingleDatePicker, & .SingleDatePickerInput': {
    width: '100%',
    textAlign: 'center',
  },

  '&.date-pick-sm, &.date-range-sm': {
    '& .DateRangePickerInput .DateInput': {
      width: '140px',
    },
    '& .SingleDatePickerInput, & .DateRangePickerInput': {
      borderRadius: '0.2rem', // $border-radius-sm
    },
    '& .SingleDatePickerInput_calendarIcon, & .DateRangePickerInput_calendarIcon': {
      padding: '6px',
    },
    '& .DateInput_input': {
      fontSize: '0.875rem', // $font-size-sm
      paddingTop: '4px',
      paddingBottom: '2px',
    },
    '& .DateRangePickerInput_arrow_svg': {
      width: '18px',
      height: '18px',
      marginTop: '-4px',
    },
  },

  '& .DateRangePickerInput__withBorder': {
    borderColor: `${theme.palette.grey[400]} !important`,
  },

  '& .DateInput': {
    borderRadius: '0.45rem', // $border-radius-lg
    color: theme.palette.grey[800],
    '& ::placeholder': {
      color: theme.palette.grey[500],
      opacity: 1,
    },
  },

  '& .DateRangePickerInput .DateInput': {
    width: '150px',
  },

  '& .DateInput_input': {
    borderRadius: '0.375rem', // $border-radius
    textAlign: 'center',
    fontSize: '1rem', // $font-size-base
    paddingTop: '0.5rem', // $input-btn-padding-y
    paddingBottom: '6px',
  },

  '& .DateInput_input__focused': {
    borderBottomColor: theme.palette.primary.main,
    borderBottomLeftRadius: '2px',
    borderBottomRightRadius: '2px',
  },

  '& .DateInput_input__disabled': {
    color: theme.palette.grey[500],
  },

  '& .CalendarDay__selected_span': {
    background: '#92e1fe',
    color: theme.palette.grey[700],
    border: `1px solid ${theme.palette.grey[100]}`,
    '&:active, &:hover': {
      background: theme.palette.info.main,
      border: `1px double ${theme.palette.info.main}`,
      color: '#fff',
    },
  },

  '& .CalendarDay__selected': {
    background: theme.palette.info.main,
    color: theme.palette.common.white,
    '&:hover': {
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },

  '& .CalendarDay__hovered_span, & .CalendarDay__hovered_span:hover': {
    background: theme.palette.grey[200],
  },
}));

export interface DateRangeProps {
  readonly from: DateTimeStr | null;
  readonly to: DateTimeStr | null;
  readonly onChange: (from: DateTimeStr | null, to: DateTimeStr | null) => void;
  readonly size?: 'sm';
  readonly disabled?: boolean;
  readonly className?: string;
  readonly isOutsideRange?: DateRangePickerShape['isOutsideRange'];
  readonly minimumNights?: number;
  readonly tz?: string;
}

export function DateRange(p: DateRangeProps) {
  const [focused, setFocused] = useState<'startDate' | 'endDate' | null>(null);
  const { $t } = useIntl();

  return (
    <StyledDateRangeWrapper
      className={classNames('date-range', p.size && `date-range-${p.size}`, p.className)}
    >
      <DateRangePicker
        startDate={p.from && (p.tz ? moment(p.from).tz(p.tz) : moment(p.from))}
        startDateId="startDate"
        endDate={p.to && (p.tz ? moment(p.to).tz(p.tz) : moment(p.to))}
        endDateId="endDate"
        minimumNights={p.minimumNights || 0}
        onDatesChange={({ startDate, endDate }) => {
          p.onChange(
            startDate &&
              toDateTimeStr(
                (p.tz ? moment(startDate).tz(p.tz) : moment(startDate))
                  .hours(0)
                  .minutes(0)
                  .seconds(0)
                  .milliseconds(0),
              ),
            endDate &&
              toDateTimeStr(
                (p.tz ? moment(endDate).tz(p.tz) : moment(endDate))
                  .hours(23)
                  .minutes(59)
                  .seconds(59)
                  .milliseconds(999),
              ),
          );
        }}
        focusedInput={focused}
        onFocusChange={setFocused}
        hideKeyboardShortcutsPanel
        disabled={p.disabled}
        isOutsideRange={p.isOutsideRange || (() => false)}
        startDatePlaceholderText={$t({
          id: 'common.form.dateRange.startDatePlaceholder',
          defaultMessage: 'Desde',
        })}
        endDatePlaceholderText={$t({
          id: 'common.form.dateRange.endDatePlaceholder',
          defaultMessage: 'Hasta',
        })}
        initialVisibleMonth={() => moment().subtract(1, 'month') as any}
      />
    </StyledDateRangeWrapper>
  );
}
